import React, { useCallback, useEffect, useRef } from 'react';
import { Form, Formik, Field } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFestivalDetailQuery } from '../../queries/useFestivalDetailQuery';
import {
  useSaveFestivalMutation,
  useChangeFestivalStatusMutation,
} from '../../queries/festivals/mutations';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import {
  Button,
  FormRow,
  Map,
  FestivalHeader,
  FileUpload,
  DateFormWidget,
  Loading,
} from '../controls';
import { FestivalPromotionImages } from './FestivalPromotionImages';

const defaultDateObject = {
  startTime: null,
  endTime: null,
  startTimeMS: null,
  endTimeMS: null,
};

const FESTIVAL_STATUSES = [
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'UNPUBLISHED', label: 'Unpublished' },
  { value: 'PREVIEW', label: 'Preview' },
  { value: 'DELETED', label: 'Deleted' },
];

function FestivalFormFields({
  festival,
  patchFestival,
  saveMutation,
  statusMutation,
  loading,
  bridgeLoading,
}) {
  const formikRef = useRef(null);

  const handleDateChange = useCallback(
    (dates) => {
      patchFestival({ ...festival, dates });
    },
    [festival, patchFestival],
  );

  const mapCoordinatesChanged = useCallback(
    (location) => {
      patchFestival({
        ...festival,
        location: { ...location, radius: 1000 },
      });
    },
    [festival, patchFestival],
  );

  const submit = async (values, { setSubmitting }) => {
    const { name, description, links, locationName } = values;
    const updatedFestival = {
      ...festival,
      name,
      description,
      links,
      location: { ...festival.location, locationName },
    };

    delete updatedFestival.startDate;

    try {
      await saveMutation.mutateAsync(updatedFestival);
    } finally {
      setSubmitting(false);
    }
  };

  const onFileUpload = useCallback(
    (fileInfo) => {
      const { filename, url } = fileInfo;
      patchFestival({ ...festival, photoURL: url, photo: filename });
    },
    [festival, patchFestival],
  );

  const onFileDelete = useCallback(() => {
    patchFestival({ ...festival, photoURL: null, photo: null });
  }, [festival, patchFestival]);

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    const current = festival.status || 'UNPUBLISHED';
    if (!festival.festivalId || status === current) {
      return;
    }
    try {
      await statusMutation.mutateAsync({
        festivalId: festival.festivalId,
        status,
      });
    } catch {
      /* toast via mutation */
    }
  };

  const save = () => {
    formikRef.current?.submitForm?.();
  };

  const validateDates = (dates) => {
    const validDates = dates.filter(
      (dateObj) => dateObj.startTimeMS && dateObj.endTimeMS,
    );
    return validDates && validDates.length === dates.length;
  };

  const validateForm = (values) => {
    const errors = {};

    if (!values.name) {
      errors.name = 'Name is required';
    }

    if (!values.description) {
      errors.description = 'Description is required';
    }

    if (!festival.dates || !validateDates(festival.dates)) {
      errors.dates = 'At least one date is required.';
    }

    if (!festival.location) {
      errors.location = 'Location is required.';
    }

    return errors;
  };

  const showLoading = bridgeLoading || loading;
  const dates = festival.dates || [defaultDateObject];
  const defaultLinks = {
    instagram: '',
    twitter: '',
    facebook: '',
    web: '',
  };
  const defaultValues = {
    name: festival.name || '',
    description: festival.description || '',
    locationName: festival.location ? festival.location.locationName : '',
    links: festival.links || defaultLinks,
    dates,
  };

  const fileList = [];

  if (festival.photoURL) {
    fileList.push({
      uid: '-1',
      name: festival.photo,
      url: festival.photoURL,
      status: 'done',
    });
  }

  if (showLoading) {
    return <Loading />;
  }

  return (
    <div>
      {festival && festival.name && (
        <FestivalHeader
          title={festival ? festival.name : 'Add New Event'}
          onSave={save}
          showExit={true}
        />
      )}
      <Formik
        ref={(node) => {
          formikRef.current = node;
        }}
        enableReinitialize
        initialValues={defaultValues}
        onSubmit={submit}
        validate={validateForm}
      >
        {() => (
          <Form>
            <FormRow label="Festival Image">
              <div className="flex min-w-0 w-full flex-row flex-nowrap items-start gap-5">
                <FileUpload
                  fullWidth={false}
                  fileList={fileList}
                  onFileUpload={onFileUpload}
                  onFileDelete={onFileDelete}
                />
                <FestivalPromotionImages
                  festivalId={festival.festivalId}
                  status={festival.status || 'UNPUBLISHED'}
                />
              </div>
            </FormRow>
            <FormRow label="Event Name" errorName="name">
              <Field
                type="text"
                name="name"
                placeholder="Type an event name here..."
              />
            </FormRow>
            {!!festival.festivalId && (
              <FormRow label="Status">
                <div
                  className="flex flex-col gap-2"
                  role="radiogroup"
                  aria-label="Festival status"
                >
                  {FESTIVAL_STATUSES.map(({ value: st, label }) => (
                    <label
                      key={st}
                      className="flex cursor-pointer items-center gap-2 leading-10"
                    >
                      <input
                        type="radio"
                        name="festival-status"
                        value={st}
                        checked={(festival.status || 'UNPUBLISHED') === st}
                        onChange={handleStatusChange}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </FormRow>
            )}
            <DateFormWidget
              label="Event Dates & Times"
              dates={dates}
              handleDateChange={handleDateChange}
              allowMultiple={true}
            />
            <FormRow label="Location">
              <Field
                type="text"
                name="locationName"
                placeholder="Type a Location Description Here"
              />
            </FormRow>
            <FormRow errorName="location">
              <Map
                location={festival.location}
                handleClick={mapCoordinatesChanged}
              />
            </FormRow>
            <FormRow label="General Information" errorName="description">
              <Field
                component="textarea"
                name="description"
                placeholder="Festival Information..."
              />
            </FormRow>
            <FormRow label={<FontAwesomeIcon icon={['fas', 'globe']} />}>
              <Field type="text" name="links.web" placeholder="Website" />
            </FormRow>
            <FormRow label={<FontAwesomeIcon icon={['fab', 'facebook']} />}>
              <Field type="text" name="links.facebook" placeholder="Facebook" />
            </FormRow>
            <FormRow label={<FontAwesomeIcon icon={['fab', 'twitter']} />}>
              <Field type="text" name="links.twitter" placeholder="Twitter" />
            </FormRow>
            <FormRow label={<FontAwesomeIcon icon={['fab', 'instagram']} />}>
              <Field
                type="text"
                name="links.instagram"
                placeholder="Instagram"
              />
            </FormRow>
            <Button
              type="button"
              onClick={save}
              style={{ float: 'right', marginTop: '1rem' }}
              mode="dark"
            >
              Save Festival
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export function FestivalForm(props) {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const setFestival = useFestivalWorkspaceStore((s) => s.setFestival);
  const patchFestival = useFestivalWorkspaceStore((s) => s.patchFestival);
  const saveMutation = useSaveFestivalMutation();
  const statusMutation = useChangeFestivalStatusMutation();

  const id = props.match?.params?.id;
  const numericId =
    id !== undefined && id !== '' && !Number.isNaN(Number(id))
      ? String(id)
      : undefined;

  const q = useFestivalDetailQuery(numericId);

  useEffect(() => {
    if (!props.match || !numericId) {
      setFestival({});
    }
  }, [props.match, numericId, setFestival]);

  useEffect(() => {
    if (q.isSuccess && q.data) {
      setFestival(q.data);
    }
  }, [q.isSuccess, q.data, setFestival]);

  const bridgeLoading = Boolean(numericId && q.isPending);
  const loading = saveMutation.isPending || statusMutation.isPending;

  return (
    <FestivalFormFields
      festival={festival}
      patchFestival={patchFestival}
      saveMutation={saveMutation}
      statusMutation={statusMutation}
      loading={loading}
      bridgeLoading={bridgeLoading}
    />
  );
}
